import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Text } from "style/v2/components/Typography";
import { isMobileDevice } from "utils/index";
import { COLOR_PALETTE } from "utils/constants";
import { getHierarchicalColors } from "utils/color";
import { buildRectangles } from "utils/treemap";
import IndustryView from "./IndustryView";
import AssetsView from "./AssetsView";
import StrategyTabItem from "./StrategyTabItem";
import {
  Card,
  CardHeader,
  CardContent,
  HeaderContent,
  SubtitleText,
  Dropdown,
  StrategyTabsContainer,
  Divider,
  ErrorText,
  MessageText,
} from "./StrategyTreemap.styles";

const StrategyTreemap = ({
  data,
  industries: industriesProp = [],
  isLoading = false,
  error = null,
  title = "Industry allocation",
  subtitle,
  strategies,
  activeStrategyIndex = 0,
  onStrategyChange,
  dropdownValue = "By industry",
  onDropdownChange,
  assets = [],
  isLoadingAssets = false,
}) => {
  const isMobile = isMobileDevice();
  const containerView = useMemo(
    () =>
      isMobile ? { width: 300, height: 260 } : { width: 600, height: 420 },
    [isMobile]
  );

  const industries = useMemo(() => {
    if (data && typeof data === "object" && Object.keys(data).length > 0) {
      return Object.entries(data)
        .map(([name, val], i) => ({
          name,
          percentage: `${(val * 100).toFixed(1)}%`,
          color: COLOR_PALETTE[i % COLOR_PALETTE.length],
          value: val * 100,
        }))
        .sort((a, b) => b.value - a.value);
    }
    return industriesProp.map((ind) => ({
      ...ind,
      value:
        typeof ind.value === "number" ? ind.value : parseFloat(ind.percentage),
    }));
  }, [data, industriesProp]);

  const rectangles = useMemo(
    () =>
      buildRectangles(industries, containerView.width, containerView.height),
    [industries, containerView]
  );

  const assetBarsData = useMemo(() => {
    const sorted = [...assets].sort(
      (a, b) =>
        parseFloat(b.percentage || b.percent) -
        parseFloat(a.percentage || a.percent)
    );
    const colors = getHierarchicalColors(sorted.length);
    return sorted.map((a, i) => ({
      name: a.symbol || a.stock || a.name || "--",
      percentage: a.percentage || a.percent,
      color: colors[i],
    }));
  }, [assets]);

  const renderContent = () => {
    if (isLoading)
      return <MessageText>Loading industry allocation data...</MessageText>;
    if (error)
      return (
        <ErrorText>
          Error loading industry allocation: {error.message || error}
        </ErrorText>
      );
    if (dropdownValue === "By assets") {
      return (
        <AssetsView
          assets={assets}
          assetBarsData={assetBarsData}
          isLoadingAssets={isLoadingAssets}
        />
      );
    }
    if (!industries.length)
      return <MessageText>No industry allocation data available</MessageText>;
    return (
      <IndustryView
        rectangles={rectangles}
        industries={industries}
        containerWidth={containerView.width}
        containerHeight={containerView.height}
        isMobile={isMobile}
      />
    );
  };

  const hasTabs = strategies?.length > 0;

  return (
    <Card>
      <CardHeader>
        <HeaderContent>
          <Text variant="cardHeading" weight="500">
            {title}
          </Text>
          {subtitle && <SubtitleText>{subtitle}</SubtitleText>}
        </HeaderContent>
        {onDropdownChange && (
          <Dropdown value={dropdownValue} onChange={onDropdownChange}>
            <option value="By industry">By industry</option>
            <option value="By assets">By assets</option>
          </Dropdown>
        )}
      </CardHeader>

      {hasTabs && (
        <>
          <StrategyTabsContainer>
            {strategies.map((strategy, index) => (
              <StrategyTabItem
                key={
                  typeof strategy === "object"
                    ? strategy.id || strategy.name
                    : strategy
                }
                strategy={strategy}
                index={index}
                activeStrategyIndex={activeStrategyIndex}
                onStrategyChange={onStrategyChange}
              />
            ))}
          </StrategyTabsContainer>
          <Divider />
        </>
      )}

      <CardContent>{renderContent()}</CardContent>
    </Card>
  );
};

StrategyTreemap.propTypes = {
  data: PropTypes.object,
  industries: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      percentage: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  strategies: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string.isRequired,
        iconUrl: PropTypes.string,
        logo: PropTypes.string,
      }),
    ])
  ),
  activeStrategyIndex: PropTypes.number,
  onStrategyChange: PropTypes.func,
  dropdownValue: PropTypes.string,
  onDropdownChange: PropTypes.func,
  assets: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      symbol: PropTypes.string,
      stock: PropTypes.string,
      company_name: PropTypes.string,
      company: PropTypes.string,
      percentage: PropTypes.string,
      percent: PropTypes.number,
      weight: PropTypes.string,
      color: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      price: PropTypes.number,
      sharePrice: PropTypes.string,
      shares: PropTypes.number,
      fromStrategy: PropTypes.string,
      from_strategy: PropTypes.string,
      strategies: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  isLoadingAssets: PropTypes.bool,
};

export default StrategyTreemap;
